import { useState } from "react";
import { Card, Flex, Row, Col, Table } from "antd";
import { getSalesTrendAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { trendMonthsStamp } from "shared/utils/download";
import { colors } from "shared/theme/color";
import { formatCurrency } from "shared/utils";
import { tnum } from "shared/components/TableText";
import { CustomMonthRange, SalesTrendChartRow, SalesTrendMonths } from "shared/types";
import PATHS from "shared/constants/paths";
import { getYtdMonthRange } from "shared/utils/dayjs";
import TrendMonthsSegmented from "./TrendMonthsSegmented";
import CustomMonthRangePicker from "./CustomMonthRangePicker";
import RevenueCompositionChart from "./RevenueCompositionChart";
import SessionsCompletedChart from "./SessionsCompletedChart";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { textPx } from "shared/constants/zoom";

const SalesTrendPage = () => {
  const { state: authState } = useAuth();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent months=ytd token.
  const ytdRange = getYtdMonthRange();
  const [months, setMonths] = useState<SalesTrendMonths>("ytd");
  const [customRange, setCustomRange] = useState<CustomMonthRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const { data, isLoading } = getSalesTrendAnalytics(months, customRange);

  // Bulan is the stretching column; the four money columns take a little
  // more than a plain money share so the month label does not dominate a
  // table of only twelve short rows.
  const columns: ReportColumn<SalesTrendChartRow>[] = [
    reportColumn<SalesTrendChartRow>("month", { dataIndex: "month_label" }),
    reportColumn<SalesTrendChartRow>("total_sales", {
      dataIndex: "cash_revenue",
      share: 15,
      // Same weight as the other columns; bold is reserved for the Total
      // summary row so emphasis means "whole period", not "this column".
      sorter: (a, b) => a.cash_revenue - b.cash_revenue,
    }),
    reportColumn<SalesTrendChartRow>("package_sales", {
      dataIndex: "paket_revenue",
      share: 15,
      sorter: (a, b) => a.paket_revenue - b.paket_revenue,
    }),
    reportColumn<SalesTrendChartRow>("membership_sales", {
      dataIndex: "membership_revenue",
      share: 15,
      sorter: (a, b) => a.membership_revenue - b.membership_revenue,
    }),
    reportColumn<SalesTrendChartRow>("booking_revenue", {
      dataIndex: "earned_revenue",
      share: 15,
      sorter: (a, b) => a.earned_revenue - b.earned_revenue,
    }),
    reportColumn<SalesTrendChartRow>("open_sessions", {
      dataIndex: "open_sessions_completed",
      sorter: (a, b) => a.open_sessions_completed - b.open_sessions_completed,
    }),
    reportColumn<SalesTrendChartRow>("private_sessions", {
      dataIndex: "private_sessions_completed",
      sorter: (a, b) => a.private_sessions_completed - b.private_sessions_completed,
    }),
  ];

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.sales.report_trend")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="ringkasan-pendapatan"
            params={customRange ? { from: customRange.from, to: customRange.to } : { months }}
            pdf={
              data && {
                fileName: `spott-ringkasan-pendapatan-${trendMonthsStamp(months, customRange)}.pdf`,
                render: async () => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "ringkasan-pendapatan",
                    studio: authState.business?.name ?? "",
                    data,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomMonthRangePicker value={customRange} onChange={setCustomRange} maxRangeMonths={12} />
            <TrendMonthsSegmented
              value={isYtdActive ? "ytd" : months}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setMonths(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card styles={{ body: { padding: 22 } }} loading={isLoading} style={{ height: "100%" }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(16), color: colors.ink }}>
                {translate("analyticsPage.sales.trend_composition_heading")}
              </div>
              <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4, marginBottom: 18 }}>
                {translate("analyticsPage.sales.trend_composition_desc")}
              </div>
              <RevenueCompositionChart data={data?.chart ?? []} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card styles={{ body: { padding: 22 } }} loading={isLoading} style={{ height: "100%" }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: textPx(16), color: colors.ink }}>
                {translate("analyticsPage.sales.trend_col_sessions")}
              </div>
              <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4, marginBottom: 18 }}>
                {translate("analyticsPage.sales.trend_sessions_desc")}
              </div>
              <SessionsCompletedChart data={data?.chart ?? []} />
            </Card>
          </Col>
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: textPx(13), color: colors.ink3, fontWeight: 500 }}>
              {translate("analyticsPage.sales.custom_range_max_months_note")}
            </span>
          </div>
          <ReportTable<SalesTrendChartRow>
            rowKey="month"
            sticky={false}
            loading={isLoading}
            columns={columns}
            dataSource={data?.chart ?? []}
            pagination={false}
            // Whole-period total per column. Summed from the DISPLAYED
            // per-month values (not a server aggregate) so the row always
            // reconciles with the rows above it. Cash and earned each total
            // down their own column only, never merged (two different
            // revenue bases). The last month is still in progress, so this
            // is a period-to-date total. Bold lives ONLY on this row (the
            // data rows are all regular weight), plus the structural
            // .analytics-total-row treatment (soft surface + heavier top
            // rule).
            summary={(rows) => {
              if (rows.length === 0) return null;
              const sum = (pick: (row: SalesTrendChartRow) => number) =>
                rows.reduce((acc, row) => acc + pick(row), 0);
              const bold = { ...tnum, fontWeight: 600 } as const;
              return (
                <Table.Summary.Row className="analytics-total-row">
                  <Table.Summary.Cell index={0}>
                    <span style={{ fontWeight: 600 }}>
                      {translate("analyticsPage.sales.trend_total")}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span style={bold}>{formatCurrency(sum((row) => row.cash_revenue))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <span style={bold}>{formatCurrency(sum((row) => row.paket_revenue))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <span style={bold}>{formatCurrency(sum((row) => row.membership_revenue))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <span style={bold}>{formatCurrency(sum((row) => row.earned_revenue))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <span style={bold}>{sum((row) => row.open_sessions_completed)}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <span style={bold}>{sum((row) => row.private_sessions_completed)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </Flex>
    </>
  );
};

export default SalesTrendPage;
